/* eslint-disable @next/next/no-img-element */
import {
  AseobiPaymentMetadataRequest,
  AseobiPaymentMetadataRequestAttire,
  Currency,
  GenerateAsoebiPaymentMetadataMutation,
  GenerateAsoebiPaymentMetadataMutationVariables,
  GetAseobiAttiresQuery,
  GetAseobiQuery,
  InitiateStripePaymentIntentMutation,
  InitiateStripePaymentIntentMutationVariables,
  PaymentProcessor,
  UserQuery,
  UserQueryVariables,
  VerifyTransactionMutation,
  VerifyTransactionMutationVariables,
} from "@src/@types/api.d";
import getSymbolFromCurrency from "currency-symbol-map";
import { Form, Formik, FormikProps } from "formik";
import React, { useEffect, useRef, useState } from "react";
import * as Yup from "yup";
import Input from "../Input";
import InputPhone from "../InputPhone";
import styles from "./styles.module.scss";
import toast, { LoaderIcon } from "react-hot-toast";
import { useMutation, useQuery } from "@apollo/client";
import { GetProfileDetails } from "@src/graphql/queries";
import {
  GenerateAsoebiPaymentMetadata,
  initiateStripePaymentIntent,
  VerifyTransaction,
} from "@src/graphql/mutations";
import Button from "../Button";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { loadStripe, Stripe } from "@stripe/stripe-js";

interface Props {
  asoebi: GetAseobiQuery | undefined;
  selectedAsoebiId: string | null;
  attires: GetAseobiAttiresQuery["getAseobiAttires"];
  quantities: {
    [key: string]: number;
  };
  finalTotal: number;
  totalQuantity: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  eventUuid: string | undefined;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  currency: Currency;
}

interface StripePaymentFormProps {
  setStep: React.Dispatch<React.SetStateAction<number>>;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  formData: Values | null;
  clientSecret: string;
}

interface Values {
  name: string;
  phoneNumber: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
}

// Stripe Payment Form Component (wrapped by Elements)
const StripePaymentForm = ({
  setStep,
  setOpen,
  formData,
  clientSecret,
}: StripePaymentFormProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);

  const [paymentElementReady, setPaymentElementReady] = useState(false);

  const [verifyTransactionFn] = useMutation<
    VerifyTransactionMutation,
    VerifyTransactionMutationVariables
  >(VerifyTransaction, {
    onCompleted: (res) => {
      if (res?.verifyTransaction?.reference) {
        toast.success("Payment successful");
        setOpen(false);
      }
    },
    onError(error) {
      if (error.message !== "unauthenticated") {
        toast.error(
          <p className="toast">{error?.message ?? "An error occured"}</p>
        );
      }
    },
  });

  const handleStripeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements || !formData) {
      return;
    }

    setIsProcessing(true);

    try {
      if (!stripe || !elements) {
        toast.error("Stripe is not ready yet. Please try again.");
        return;
      }

      const { error: submitError } = await elements.submit();
      if (submitError) {
        toast.error(submitError.message || "Payment form validation failed");
        return;
      }

      const result = await stripe.confirmPayment({
        clientSecret,
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment/success`,
          payment_method_data: {
            billing_details: {
              name: formData.name,
              email: formData.email,
              address: {
                line1: formData.address,
                city: formData.city,
                postal_code: formData.zipCode,
              },
            },
          },
        },
        redirect: "if_required",
      });

      if (result.error) {
        toast.error(result.error.message || "Payment failed");
        return;
      }
      if (result.paymentIntent.status === "succeeded") {
        verifyTransactionFn({
          variables: {
            reference: result?.paymentIntent?.id,
            provider: PaymentProcessor.Stripe,
          },
        });
        return;
      }
    } catch (error) {
      console.error(error);
      toast.error("An error occurred during payment");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form onSubmit={handleStripeSubmit} className="flex flex-col gap-4">
      {!paymentElementReady ? (
        <div className="flex w-full items-center justify-center p-4">
          <LoaderIcon />
        </div>
      ) : null}

      <PaymentElement
        onLoaderStart={() => setPaymentElementReady(false)}
        onReady={() => setPaymentElementReady(true)}
      />
      <div className="flex gap-2">
        <button
          className="w-full py-2 border border-gray-500 hover:bg-gray-200 text-black font-inter rounded-md transition duration-300"
          type="button"
          onClick={() => setStep(2)}
          disabled={isProcessing}
        >
          Go back
        </button>
        <Button
          type="submit"
          label="Pay with Stripe"
          className="w-full py-2 px-1 hover:bg-[#34544F] bg-[#4e7e77] text-white font-inter rounded-md transition duration-300 flex items-center justify-center disabled:cursor-not-allowed disabled:bg-[#cccccc]"
          disabled={!stripe || isProcessing}
        >
          <p>{isProcessing ? "Processing..." : "Pay with Stripe"}</p>
        </Button>
      </div>
    </form>
  );
};

// Main Stripe Payment Component
const AsoebiStripePayment = ({
  asoebi,
  selectedAsoebiId,
  attires,
  quantities,
  finalTotal,
  totalQuantity,
  setStep,
  eventUuid,
  setOpen,
  currency,
}: Props) => {
  // Define Yup schema
  const OrderFormSchema = Yup.object().shape({
    name: Yup.string()
      .min(2, "Name must be at least 2 characters")
      .max(100, "Name must be less than 100 characters")
      .required("Name is required"),
    email: Yup.string()
      .email("Please enter a valid email address")
      .required("Email is required"),
    address: Yup.string()
      .min(5, "Address must be at least 5 characters")
      .required("Address is required"),
    city: Yup.string()
      .min(2, "City must be at least 2 characters")
      .required("City is required"),
    state: Yup.string()
      .min(2, "State must be at least 2 characters")
      .required("State is required"),
    zipCode: Yup.string()
      .min(3, "Zip code must be at least 3 characters")
      .required("Zip code is required"),
  });

  const { data: profile } = useQuery<UserQuery, UserQueryVariables>(
    GetProfileDetails
  );
  const [phoneNumber, setPhoneNumber] = useState("");
  const formikRef = useRef<FormikProps<Values>>(null);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  // const [clientSecret, setClientSecret] = useState<string | null>(
  //   "pi_3SNU4k4F2Tc18uw60UjFzWqI_secret_egguTZVoyV1gscQUnRtnTufe7"
  // );
  const [stripePromise, setStripePromise] =
    useState<Promise<Stripe | null> | null>(null);
  const [formData, setFormData] = useState<Values | null>(null);

  // Mutation for generating payment metadata
  const [generatePaymentMetadata] = useMutation<
    GenerateAsoebiPaymentMetadataMutation,
    GenerateAsoebiPaymentMetadataMutationVariables
  >(GenerateAsoebiPaymentMetadata, {
    onError(error) {
      toast.error(
        <p className="font-inter">
          {error?.message ?? "Failed to generate payment metadata"}
        </p>
      );
    },
  });

  // Mutation for initiating Stripe payment
  const [initiateStripe] = useMutation<
    InitiateStripePaymentIntentMutation,
    InitiateStripePaymentIntentMutationVariables
  >(initiateStripePaymentIntent, {
    onError(error) {
      toast.error(
        <p className="font-inter">
          {error?.message ?? "Failed to initiate Stripe payment"}
        </p>
      );
    },
  });

  // Initialize Stripe
  useEffect(() => {
    const stripeKey = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY;
    if (stripeKey) {
      setStripePromise(loadStripe(stripeKey));
    } else {
      toast.error("Stripe configuration is missing");
    }
  }, []);

  const handleSubmit = async (values: Values) => {
    if (!eventUuid || !selectedAsoebiId) {
      toast.error("Event ID or Asoebi ID is missing");
      return;
    }

    // Store form data for later use
    setFormData(values);

    // Prepare attire data for mutation
    const attireInput: AseobiPaymentMetadataRequestAttire[] =
      attires
        ?.filter((item) => (quantities[item.id] || 0) > 0)
        .map((item) => ({
          aseobi_id: item.aseobi_id,
          attire_id: item.id,
          quantity: 1,
          yards: quantities[item.id],
        })) ?? [];

    // Prepare mutation input
    const input: AseobiPaymentMetadataRequest = {
      aseobi_id: selectedAsoebiId,
      attires: attireInput,
      contact_email: values.email,
      contact_phone: phoneNumber,
      currency: currency,
      delivery_address: values.address,
      delivery_city: values.city,
      delivery_state: values.state,
      delivery_zip_code: values.zipCode,
      note: "Asoebi order payment",
      payment_provider: PaymentProcessor.Stripe,
      user_id: profile?.user?.uuid ?? "",
    };

    try {
      // Step 1: Call mutation to generate payment metadata
      const { data: mutationData } = await generatePaymentMetadata({
        variables: {
          event_id: eventUuid,
          input,
        },
      });

      const paymentMeta = mutationData?.generateAsoebiPaymentMetadata;
      const paymentReference = paymentMeta?.payment_reference;

      if (!paymentReference) {
        toast.error("Failed to generate payment reference");
        return;
      }

      // Step 2: Call initiateStripePaymentIntent to get client secret
      const { data: stripeData } = await initiateStripe({
        variables: {
          args: {
            customer_email: values.email,
            event_id: eventUuid,
            inawo_payment_reference: paymentReference,
          },
        },
      });

      const secret = stripeData?.initiateStripePaymentIntent?.client_secret;

      if (!secret) {
        toast.error("Failed to initialize Stripe payment");
        return;
      }

      // Set client secret to render Stripe Elements
      setClientSecret(secret);
    } catch (error) {
      toast.error("Failed to initiate payment");
      console.error(error);
    }
  };

  return (
    <div className="flex flex-col flex-grow h-full min-h-0">
      <div className="mb-4 flex-shrink-0">
        <h2 className="mb-4 font-inter text-2xl font-semibold">
          {
            asoebi?.getAseobi?.find((item) => item.id === selectedAsoebiId)
              ?.name
          }
        </h2>
        <p className="text-sm text-gray-500 font-inter">Finish your order</p>
      </div>

      <div className="border border-gray-300 rounded-xl py-4 flex flex-col gap-4 justify-center items-center w-full mb-4 flex-shrink-0">
        <div className="flex">
          {attires
            ?.filter((item) => (quantities[item.id] || 0) > 0)
            .slice(0, 3)
            .map((item) => (
              <div key={item.id} className="w-14 h-14">
                {item.images && item.images[0] ? (
                  <img
                    src={item.images[0]}
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-red-300"></div>
                )}
              </div>
            ))}
        </div>

        <p className="text-lg font-inter">
          {attires?.[0]?.currency_iso
            ? getSymbolFromCurrency(attires[0].currency_iso)
            : ""}
          {finalTotal.toLocaleString()}
        </p>

        <p className="text-sm text-gray-500 font-inter">
          {totalQuantity} items, {totalQuantity} yards
        </p>
      </div>

      {!clientSecret ? (
        <Formik
          initialValues={{
            name: "",
            phoneNumber: "",
            email: "",
            address: "",
            city: "",
            state: "",
            zipCode: "",
          }}
          validationSchema={OrderFormSchema}
          onSubmit={handleSubmit}
          innerRef={formikRef}
        >
          {(props: FormikProps<Values>) => (
            <Form
              className={`${styles["form-container"]} flex flex-col h-full min-h-0`}
            >
              <div className="flex flex-col gap-4 flex-1 overflow-y-auto thin-scrollbar pr-2">
                <Input
                  type="text"
                  name="name"
                  label="Name"
                  placeholder="Type your full name"
                />
                <InputPhone
                  name="phoneNumber"
                  label="Phone number"
                  placeholder="Type your phone number"
                  value={phoneNumber}
                  setValue={setPhoneNumber}
                  className="font-inter"
                />
                <Input
                  type="email"
                  name="email"
                  label="Email address"
                  placeholder="Type your email address"
                />
                <Input
                  type="text"
                  name="address"
                  label="Address"
                  placeholder="Type your delivery address"
                />
                <div className="flex gap-2">
                  <Input
                    type="text"
                    name="city"
                    label="City"
                    placeholder="Type your city"
                  />
                  <Input
                    type="text"
                    name="state"
                    label="State"
                    placeholder="Type your state"
                  />
                </div>
                <Input
                  type="text"
                  name="zipCode"
                  label="Zip Code"
                  placeholder="Type your zip code"
                />
              </div>
              <div className="flex gap-2 flex-shrink-0 pt-4 border-t border-gray-200 mt-4">
                <button
                  className="w-full py-2 border border-gray-500 hover:bg-gray-200 text-black font-inter rounded-md transition duration-300"
                  type="button"
                  onClick={() => setStep(2)}
                >
                  Go back
                </button>
                <Button
                  type="submit"
                  label="Continue to Payment"
                  className="w-full py-2 px-1 hover:bg-[#34544F] bg-[#4e7e77] text-white font-inter rounded-md transition duration-300 flex items-center justify-center disabled:cursor-not-allowed disabled:bg-[#cccccc]"
                  disabled={
                    phoneNumber === "" ||
                    props.values.name === "" ||
                    props.values.email === "" ||
                    props.values.address === "" ||
                    props.values.city === "" ||
                    props.values.state === "" ||
                    props.values.zipCode === ""
                  }
                >
                  <p>Continue to Payment</p>
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      ) : (
        stripePromise &&
        clientSecret && (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: {
                theme: "stripe",
              },
            }}
          >
            <StripePaymentForm
              setStep={setStep}
              setOpen={setOpen}
              formData={formData}
              clientSecret={clientSecret}
            />
          </Elements>
        )
      )}
    </div>
  );
};

export default AsoebiStripePayment;
